<template>
    <li>
      <div @click="toggleOpen" style="cursor: pointer;">
        <span v-if="hasChildren">
          <span style="font-size: 20px;" v-if="!isOpen">▸</span>
          <span style="font-size: 20px;" v-if="isOpen">▾</span>
        </span>
        <span v-if="['unit', 'location', 'area'].includes(type)">
            {{ node.name }}
        </span>
        <span v-if="type === 'device'">
            <input style="margin: 0 10px 0 10px;" :checked="recyclingJobList.includes(deviceId)" v-if="recycling" type="checkbox" aria-label="restart device in recycled job" @click="(event) => event.stopPropagation()" @change="(event) => {
                if (recyclingJobList.includes(deviceId)) recyclingJobList = recyclingJobList.filter((item) => item !== deviceId);
                else recyclingJobList.push(deviceId);
            }">
            {{ deviceId }} ({{ node.model }} {{ node.subModel }}) 
            <small 
                v-if="Array.isArray(devices) 
                    && devices.find((device) => device.deviceId === deviceId) && devices.find((device) => device.deviceId === deviceId).errorDetected">
                ( At least one error occured. See IOT Service logs. )
            </small>
        </span>
        <span v-if="type === 'state'">
            <span :id="`task-details-${node.taskId}-${deviceId}`">
                <span style="width: 25px; display: inline-flex; justify-content: flex-start;">
                    <font-awesome-icon :style="{ color: '#66be6d' }" v-if="['Command Success'].includes(node.taskStatus)" icon="check" />
                    <font-awesome-icon :style="{ color: '#eed202 ' }" v-if='["Command Failure", "Command Cancelled", "Command TimeOut"].includes(node.taskStatus)' icon="exclamation-triangle" />
                    <img v-if="!['Command Success', 'Command Failure', 'Command Cancelled', 'Command TimeOut'].includes(node.taskStatus)" src="../../assets/images/loader-a2.gif" alt="In progress" width="20px" />
                </span>
                <span style="display: inline-flex; font-weight: 600; width: 150px;">
                    Task {{ getLastDigits(node.taskId) }} ({{ taskTypeForTask(node.taskId) }})
                </span>
                <span style="width: 400px; display: inline-flex; justify-content: flex-start; font-weight: 600; gap: 10px;">
                    <strong v-if="node.taskStatus">
                        {{ node.taskStatus }} 
                    </strong>
                    <strong v-if="!node.taskStatus">Command Queued</strong>
                    <span v-if="node.taskStatus !== 'Command Success' && node.reason">
                        <small style="font-weight: 200;">
                            {{ node.reason }}
                        </small>
                    </span>
                </span>
            </span>
            <b-popover :target="`task-details-${node.taskId}-${deviceId}`" id="task-details" 
                triggers="hover" placement="right"
                v-bind:title="'Task Details'">
                <span v-if="node.aidamId" style="display: block; font-weight: 600;">
                    <strong style="width: 205px; display: inline-flex;">Aidam ID (IOT)</strong> 
                    {{ node.aidamId }} 
                </span>
                <span v-if="node.iotId" style="display: block; font-weight: 600;">
                    <strong style="width: 205px; display: inline-flex;">Task ID (IOT)</strong> 
                    {{ node.iotId }} 
                </span>
                <span v-if="node.startedAt" style="display: block; font-weight: 600;">
                    <strong style="width: 205px; display: inline-flex;">Started At</strong> 
                    {{node.startedAt && node.startedAt.length > 0 ? moment(node.startedAt).format("dddd, MMMM Do YYYY, h:mm:ss a") : 'Not Started'}}
                </span>
                <span v-if="node.completedAt" style="display: block; font-weight: 600;">
                    <strong style="width: 205px; display: inline-flex;">Completed At</strong> 
                    {{node.completedAt && node.completedAt.length > 0 ? moment(node.completedAt).format("dddd, MMMM Do YYYY, h:mm:ss a") : 'Incomplete'}}
                </span>
            </b-popover>
            <span v-if="typeof node.startedAt === 'string' && node.startedAt.length && !node.completedAt && taskTypeForTask(node.taskId) === 'delay'" style="display: block; font-weight: 600;">
                <span v-if="timeRemainingForTask(node.startedAt, node.taskId) > 0">
                    An estimated {{ timeRemainingForTask(node.startedAt, node.taskId) }} minute(s) until {{ taskTypeForTask(node.taskId) }} completes.
                </span>
                <span v-if="timeRemainingForTask(node.startedAt, node.taskId) <= 0">
                    Delay will complete momentarily in under a minute.
                </span>
            </span>
        </span>
      </div>
      <ul v-if="isOpen && hasChildren">
        <TreeNode 
            v-for="item in node.units" :default-open="defaultOpen" :recycling="recycling" :recyclingJobList="recyclingJobList"
            :key="item.id" :node="item" :type="'unit'" :tasks="tasks" :devices="devices" />
        <TreeNode
            v-for="item in node.devices" :default-open="defaultOpen" :recycling="recycling" :recyclingJobList="recyclingJobList"
            :key="item.id" :node="item" :type="'device'" :tasks="tasks" :devices="devices"
            :deviceId="item.deviceId" />
        <TreeNode 
            v-for="item in node.states" :default-open="defaultOpen" :recycling="recycling" :recyclingJobList="recyclingJobList"
            :key="item.id" :node="item" :type="'state'" :tasks="tasks" :devices="devices"
            :deviceId="deviceId" />
        <TreeNode 
            v-for="item in node.areas" :default-open="defaultOpen" :recycling="recycling" :recyclingJobList="recyclingJobList"
            :key="item.id" :node="item" :type="'area'" :tasks="tasks" :devices="devices"
            />
      </ul>
    </li>
</template>
  
<script>
    const moment = require("moment");

    /**
     * Recursive Vue component to display a single node of the tree.
     * Supports expanding/collapsing children.
     * 
     * @component
     * @prop {Object} node - The current node object being rendered. Should contain at least an `id`, and optionally `units`, `areas`, `devices`, and `states` properties.
     */
    export default {
        name: "TreeNode",
        data() {
            return { 
                isOpen: this.defaultOpen,
                moment: moment,
            }
        },
        watch: {
            defaultOpen(newVal) {
                this.isOpen = newVal; 
            }
        },
        props: {
            node: {
                type: Object,
                required: true
            },
            type: {
                type: String,
                required: true
            },
            tasks: {
                type: Object,
                required: true
            },
            devices: {
                type: Object,
                required: true
            },
            deviceId: {
                type: String,
                required: false
            },
            defaultOpen: {
                type: Boolean,
                default: false
            },
            recycling: {
                type: Boolean,
                required: false,
                },
            recyclingJobList: {
                type: Object,
                required: false,
            }
        },
        computed: {
            hasChildren() {
                return (this.node.units && this.node.units.length) || (this.node.areas && this.node.areas.length) || (this.node.areas && this.node.areas.length) || (this.node.devices && this.node.devices.length) || (this.node.states && this.node.states.length);
            }
        },
        methods: {
            toggleOpen() {
                this.isOpen = !this.isOpen;
            },
            taskTypeForTask(id) {
                const taskId = this.getLastDigits(id);
                const type = 
                    Array.isArray(this.tasks) && this.tasks[taskId - 1] && 
                    this.tasks[taskId - 1].type ? 
                    this.tasks[taskId - 1].type : "task";
                return type;
            },
            timeRemainingForTask(startedAt, id) {
                const taskId = this.getLastDigits(id);
                const minutes = 
                    Array.isArray(this.tasks) && this.tasks[taskId - 1] && 
                    this.tasks[taskId - 1].payload && 
                    this.tasks[taskId - 1].payload.minutes ? 
                    this.tasks[taskId - 1].payload.minutes : false;
                return minutes ? this.timeRemaining(startedAt, minutes) : false;
            },
            timeRemaining(startTime, interval, intervalFormat = 'minutes', outputFormat = 'minutes') {
                const formatMap = {
                    days: 86400000,
                    hours: 3600000,
                    minutes: 60000,
                    seconds: 1000,
                    ms: 1,
                }
                startTime = new Date(startTime);
                const endTime = new Date(startTime.getTime() + interval * formatMap[intervalFormat]);
                const currentTime = new Date();
                const remainingTimeMs = endTime - currentTime;
                return Math.floor(remainingTimeMs / formatMap[outputFormat]);
            },
            getLastDigits(inputStr) {
                const regex = /(\d+)(?!.*\d)/;
                const match = inputStr.match(regex);
                return match ? match[1] : 'No digits found.';
            },
        }
    };
</script>
  
<style scoped>
    ul {
        list-style-type: none;
        padding-left: 20px;
        margin-bottom: 0;
    }

    li {
        margin: 10px 0;
    }
</style>
